import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PROFILE_IDS,
  PROFILE_LABELS,
  type GlossaryEntry,
  type IngestJob,
  type IngestJobStatus,
  type IngestJobSummary,
  type ProfileId,
  type StoredReport,
  type TermCandidate,
} from '@cixing/shared';
import {
  adminLogin,
  applyIngestJob,
  fetchGlossaryEntries,
  fetchGlossaryIssues,
  fetchIngestJob,
  fetchIngestJobs,
  fetchReports,
  patchCandidate,
  reloadGlossary,
  reportToGlossary,
  uploadIngestFile,
  type GlossaryIssuesResponse,
} from '../admin-api.js';
import {
  fetchLecture,
  fetchLectures,
  fetchMe,
  type LectureMeta,
  type LectureSegment,
} from '../api.js';
import { downloadFile } from '../export/formats.js';

/**
 * 管理画面(/admin)。
 *
 * 4つの仕事をタブで分ける:
 *  - 用語集: 現在の辞書の中身と健全性の確認
 *  - 資料取込: 資料→用語候補→レビュー→辞書反映 のパイプライン操作
 *  - 誤訳レポート: 現地からの報告を確認し、用語集へ還流する
 *  - 講義メモ: 現地メンバー全員の講義記録を日本から閲覧する
 *
 * 通常画面とはログインが別(scope: admin)。現場の利用者に管理機能を見せない。
 * 管理者ログインは user Cookie も併せて発行されるため、講義APIはそのまま呼べる。
 */

type Tab = 'glossary' | 'ingest' | 'reports' | 'lectures';

export function Admin(): JSX.Element {
  const [auth, setAuth] = useState<'loading' | 'login' | 'ready'>('loading');
  const [tab, setTab] = useState<Tab>('glossary');

  useEffect(() => {
    fetchMe()
      .then((me) => setAuth(me.isAdmin ? 'ready' : 'login'))
      .catch(() => setAuth('login'));
  }, []);

  if (auth === 'loading') {
    return <div className="screen">読み込んでいます…</div>;
  }
  if (auth === 'login') {
    return <AdminLogin onSuccess={() => setAuth('ready')} />;
  }

  return (
    <div className="screen" style={{ maxWidth: 960 }}>
      <h1 className="app-title">管理画面</h1>
      <p className="app-subtitle">用語集の管理・資料からの用語取込・誤訳レポートの確認</p>

      <div className="row" style={{ marginBottom: 18 }}>
        <button className={tab === 'glossary' ? 'primary' : ''} onClick={() => setTab('glossary')}>
          用語集
        </button>
        <button className={tab === 'ingest' ? 'primary' : ''} onClick={() => setTab('ingest')}>
          資料取込
        </button>
        <button className={tab === 'reports' ? 'primary' : ''} onClick={() => setTab('reports')}>
          誤訳レポート
        </button>
        <button className={tab === 'lectures' ? 'primary' : ''} onClick={() => setTab('lectures')}>
          講義メモ
        </button>
      </div>

      {tab === 'glossary' && <GlossaryTab />}
      {tab === 'ingest' && <IngestTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'lectures' && <LecturesTab />}
    </div>
  );
}

function AdminLogin({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await adminLogin(code);
      onSuccess();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'ログインできませんでした。');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <h1 className="app-title">管理画面</h1>
      <p className="app-subtitle">管理者用アクセスコードを入力してください。</p>
      {error && <div className="notice error">{error}</div>}
      <form onSubmit={(e) => void handleSubmit(e)}>
        <div className="field">
          <label>管理者用アクセスコード</label>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
          />
        </div>
        <button type="submit" className="primary start-button" disabled={busy || code === ''}>
          {busy ? '確認しています…' : 'ログイン'}
        </button>
      </form>
    </div>
  );
}

// ---- 用語集タブ ------------------------------------------------------------

function GlossaryTab(): JSX.Element {
  const [issues, setIssues] = useState<GlossaryIssuesResponse>();
  const [entries, setEntries] = useState<GlossaryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [profile, setProfile] = useState<ProfileId | ''>('');
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();

  const loadIssues = useCallback((): void => {
    fetchGlossaryIssues()
      .then(setIssues)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : '用語集の状態を取得できませんでした。'),
      );
  }, []);

  useEffect(loadIssues, [loadIssues]);

  useEffect(() => {
    fetchGlossaryEntries(q, profile)
      .then((data) => {
        setEntries(data.entries);
        setTotal(data.total);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : '用語一覧を取得できませんでした。'),
      );
  }, [q, profile]);

  const handleReload = async (): Promise<void> => {
    setError(undefined);
    setMessage(undefined);
    try {
      const result = await reloadGlossary();
      setMessage(
        `再読み込みしました(version: ${result.version} / ${result.stats.total} 語 / エラー ${result.errorCount} 件)`,
      );
      loadIssues();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '再読み込みに失敗しました。');
    }
  };

  return (
    <div>
      {error && <div className="notice error">{error}</div>}
      {message && <div className="notice">{message}</div>}

      {issues && (
        <div className="notice">
          全 {issues.stats.total} 語(有効 {issues.stats.enabled})・version {issues.version}
          <br />
          {PROFILE_IDS.map((id) => `${PROFILE_LABELS[id]}: ${issues.stats.byProfile[id] ?? 0}`).join(
            ' / ',
          )}
        </div>
      )}

      {issues && issues.errors.length > 0 && (
        <div className="notice error">
          エラー {issues.errors.length} 件(該当行は辞書に載っていません):
          {issues.errors.slice(0, 10).map((i, idx) => (
            <div key={idx}>
              {i.sourceFile}:{i.sourceLine} — {i.message}
            </div>
          ))}
        </div>
      )}
      {issues && issues.warnings.length > 0 && (
        <div className="notice">警告 {issues.warnings.length} 件(CSV を確認してください)</div>
      )}

      <div className="row" style={{ marginBottom: 12 }}>
        <input
          placeholder="用語を検索(中国語・日本語)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <select
          value={profile}
          onChange={(e) => setProfile(e.target.value as ProfileId | '')}
          style={{ width: 'auto' }}
        >
          <option value="">全プロファイル</option>
          {PROFILE_IDS.map((id) => (
            <option key={id} value={id}>
              {PROFILE_LABELS[id]}
            </option>
          ))}
        </select>
        <a href="/api/admin/glossary/export" download>
          <button type="button">CSV ダウンロード</button>
        </a>
        <button onClick={() => void handleReload()}>再読み込み</button>
      </div>

      <p className="hint">{total} 件が該当(表示は先頭 1000 件まで)</p>
      <div className="table-scroll">
        <table className="admin-table">
          <thead>
            <tr>
              <th>中国語</th>
              <th>日本語訳</th>
              <th>プロファイル</th>
              <th>優先度</th>
              <th>有効</th>
              <th>メモ</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e, idx) => (
              <tr key={`${e.sourceFile}:${e.sourceLine}:${idx}`}>
                <td className="zh-cell">{e.sourceZh}</td>
                <td>{e.targetJa}</td>
                <td>{PROFILE_LABELS[e.profile]}</td>
                <td>{e.priority}</td>
                <td>{e.enabled ? '○' : '×'}</td>
                <td className="dim-cell">{e.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- 資料取込タブ ----------------------------------------------------------

const STATUS_LABELS: Record<IngestJobStatus, string> = {
  extracting: 'テキスト抽出中',
  analyzing: '用語抽出中',
  review: 'レビュー待ち',
  applied: '適用済み',
  failed: '失敗',
  'needs-ocr': 'OCRが必要',
};

function statusBadgeClass(status: IngestJobStatus): string {
  if (status === 'applied') return 'badge open';
  if (status === 'failed' || status === 'needs-ocr') return 'badge closed';
  if (status === 'review') return 'badge reconnecting';
  return 'badge';
}

function IngestTab(): JSX.Element {
  const [jobs, setJobs] = useState<IngestJobSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [error, setError] = useState<string>();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadJobs = useCallback((): void => {
    fetchIngestJobs()
      .then((data) => setJobs(data.jobs))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'ジョブ一覧を取得できませんでした。'),
      );
  }, []);

  useEffect(loadJobs, [loadJobs]);

  const handleUpload = async (): Promise<void> => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('ファイルを選択してください。');
      return;
    }
    setUploading(true);
    setError(undefined);
    try {
      const { jobId } = await uploadIngestFile(file);
      if (fileRef.current) fileRef.current.value = '';
      loadJobs();
      setSelectedId(jobId); // アップロード直後から進捗を見られるように、そのまま詳細へ。
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'アップロードに失敗しました。');
    } finally {
      setUploading(false);
    }
  };

  if (selectedId) {
    return (
      <JobDetail
        jobId={selectedId}
        onBack={() => {
          setSelectedId(undefined);
          loadJobs();
        }}
      />
    );
  }

  return (
    <div>
      {error && <div className="notice error">{error}</div>}

      <div className="field">
        <label>資料をアップロード(PDF / DOCX / TXT / MD)</label>
        <div className="row">
          <input type="file" accept=".pdf,.docx,.txt,.md" ref={fileRef} style={{ flex: 1 }} />
          <button className="primary" onClick={() => void handleUpload()} disabled={uploading}>
            {uploading ? 'アップロード中…' : '取込開始'}
          </button>
        </div>
        <p className="hint">
          アップロード後、自動でテキスト抽出と用語候補の抽出が始まります(数十秒〜数分)。
        </p>
      </div>

      {jobs.length === 0 ? (
        <div className="empty">まだ取込ジョブがありません。資料をアップロードしてください。</div>
      ) : (
        <div className="profile-list">
          {jobs.map((job) => (
            <button
              key={job.id}
              className="profile-item"
              style={{ textAlign: 'left' }}
              onClick={() => setSelectedId(job.id)}
            >
              <span style={{ flex: 1 }}>
                {job.originalFilename}
                <span className="hint" style={{ display: 'block' }}>
                  {formatDate(job.createdAt)}・候補 {job.candidateCount} 件・採用{' '}
                  {job.acceptedCount} 件
                </span>
              </span>
              <span className={statusBadgeClass(job.status)}>{STATUS_LABELS[job.status]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function JobDetail({ jobId, onBack }: { jobId: string; onBack: () => void }): JSX.Element {
  const [job, setJob] = useState<IngestJob>();
  const [error, setError] = useState<string>();
  const [conflictOnly, setConflictOnly] = useState(false);
  const [applying, setApplying] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback((): void => {
    fetchIngestJob(jobId)
      .then((data) => setJob(data.job))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'ジョブを取得できませんでした。'),
      );
  }, [jobId]);

  useEffect(load, [load]);

  // 抽出中は2秒ごとにポーリングする。この画面を開いたまま完了を待てるように。
  useEffect(() => {
    if (!job || (job.status !== 'extracting' && job.status !== 'analyzing')) return;
    const timer = setInterval(load, 2000);
    return () => clearInterval(timer);
  }, [job, load]);

  const updateCandidate = async (
    candidateId: string,
    patch: { decision?: 'accept' | 'reject' | 'pending'; targetJa?: string; profile?: ProfileId },
  ): Promise<void> => {
    try {
      const result = await patchCandidate(jobId, candidateId, patch);
      setJob(result.job);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '候補を更新できませんでした。');
    }
  };

  const handleBulkAccept = async (): Promise<void> => {
    if (!job) return;
    setBulkBusy(true);
    setError(undefined);
    try {
      // duplicate は既に登録済みなので一括採用の対象にしない。
      const targets = job.candidates.filter(
        (c) => c.confidence >= 0.8 && c.kind !== 'duplicate' && c.decision !== 'accept',
      );
      for (const candidate of targets) {
        await patchCandidate(jobId, candidate.id, { decision: 'accept' });
      }
      load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '一括採用に失敗しました。');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleApply = async (): Promise<void> => {
    setApplying(true);
    setError(undefined);
    try {
      const result = await applyIngestJob(jobId);
      setJob(result.job);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '適用に失敗しました。');
    } finally {
      setApplying(false);
    }
  };

  if (!job) {
    return (
      <div>
        <div className="row">
          <button onClick={onBack}>← 一覧へ</button>
        </div>
        {error ? <div className="notice error">{error}</div> : <p>読み込んでいます…</p>}
      </div>
    );
  }

  const acceptedCount = job.candidates.filter((c) => c.decision === 'accept').length;
  const visible = conflictOnly
    ? job.candidates.filter((c) => c.kind === 'conflict')
    : job.candidates;

  return (
    <div>
      <div className="row" style={{ marginBottom: 12 }}>
        <button onClick={onBack}>← 一覧へ</button>
        <span className={statusBadgeClass(job.status)}>{STATUS_LABELS[job.status]}</span>
        <span className="hint">
          {job.originalFilename}({Math.round(job.byteSize / 1024)} KB・抽出 {job.extractedChars}{' '}
          文字)
        </span>
      </div>

      {error && <div className="notice error">{error}</div>}
      {job.errorMessage && <div className="notice error">{job.errorMessage}</div>}

      {(job.status === 'extracting' || job.status === 'analyzing') && (
        <div className="notice">
          {STATUS_LABELS[job.status]}です。このまましばらくお待ちください(自動で更新されます)。
        </div>
      )}

      {job.status === 'applied' && job.appliedCsvPath && (
        <div className="notice">
          適用済み: <code>{job.appliedCsvPath}</code> に {acceptedCount}{' '}
          件を書き出し、辞書へ反映しました。このファイルを削除すればこの取込だけ元に戻せます。
        </div>
      )}

      {(job.status === 'review' || job.status === 'applied') && (
        <>
          <div className="row" style={{ marginBottom: 12 }}>
            <button onClick={() => void handleBulkAccept()} disabled={bulkBusy}>
              {bulkBusy ? '処理中…' : '信頼度0.8以上を一括採用'}
            </button>
            <label className="row" style={{ gap: 6 }}>
              <input
                type="checkbox"
                style={{ width: 20, minHeight: 0 }}
                checked={conflictOnly}
                onChange={(e) => setConflictOnly(e.target.checked)}
              />
              conflict のみ表示
            </label>
            <div className="spacer" />
            <button
              className="primary"
              onClick={() => void handleApply()}
              disabled={applying || acceptedCount === 0}
            >
              {applying ? '適用中…' : `採用 ${acceptedCount} 件を用語集へ適用`}
            </button>
          </div>

          <div className="table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>採用</th>
                  <th>中国語</th>
                  <th>日本語訳(編集可)</th>
                  <th>プロファイル</th>
                  <th>信頼度</th>
                  <th>回数</th>
                  <th>状態</th>
                  <th>文脈・理由</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((candidate) => (
                  <CandidateRow
                    key={candidate.id}
                    candidate={candidate}
                    onPatch={(patch) => void updateCandidate(candidate.id, patch)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {visible.length === 0 && (
            <div className="empty">表示できる候補がありません。</div>
          )}
        </>
      )}
    </div>
  );
}

function CandidateRow({
  candidate,
  onPatch,
}: {
  candidate: TermCandidate;
  onPatch: (patch: {
    decision?: 'accept' | 'reject' | 'pending';
    targetJa?: string;
    profile?: ProfileId;
  }) => void;
}): JSX.Element {
  // 入力中の再描画で打鍵が消えないよう、訳語はローカルに持ち、確定(blur)で送る。
  const [targetJa, setTargetJa] = useState(candidate.targetJa);
  useEffect(() => setTargetJa(candidate.targetJa), [candidate.targetJa]);

  const kindLabel: Record<TermCandidate['kind'], string> = {
    new: '新規',
    duplicate: '登録済み',
    conflict: '競合',
  };

  return (
    <tr className={candidate.decision === 'reject' ? 'row-rejected' : ''}>
      <td>
        <input
          type="checkbox"
          style={{ width: 20, minHeight: 0 }}
          checked={candidate.decision === 'accept'}
          onChange={(e) => onPatch({ decision: e.target.checked ? 'accept' : 'reject' })}
        />
      </td>
      <td className="zh-cell">{candidate.sourceZh}</td>
      <td>
        <input
          value={targetJa}
          onChange={(e) => setTargetJa(e.target.value)}
          onBlur={() => {
            if (targetJa.trim() !== '' && targetJa !== candidate.targetJa) {
              onPatch({ targetJa: targetJa.trim() });
            }
          }}
          style={{ minWidth: 140, minHeight: 36, padding: '6px 10px' }}
        />
        {candidate.kind === 'conflict' && candidate.existingJa && (
          <div className="hint">既存訳: {candidate.existingJa}</div>
        )}
      </td>
      <td>
        <select
          value={candidate.profile}
          onChange={(e) => onPatch({ profile: e.target.value as ProfileId })}
          style={{ minHeight: 36, padding: '6px 10px', width: 'auto' }}
        >
          {PROFILE_IDS.map((id) => (
            <option key={id} value={id}>
              {PROFILE_LABELS[id]}
            </option>
          ))}
        </select>
      </td>
      <td>{Math.round(candidate.confidence * 100)}%</td>
      <td>{candidate.frequency}</td>
      <td>
        <span className={candidate.kind === 'conflict' ? 'badge closed' : 'badge'}>
          {kindLabel[candidate.kind]}
        </span>
      </td>
      <td className="dim-cell">
        {candidate.contextZh}
        {candidate.reason && <div className="hint">{candidate.reason}</div>}
      </td>
    </tr>
  );
}

// ---- 誤訳レポートタブ ------------------------------------------------------

function ReportsTab(): JSX.Element {
  const [reports, setReports] = useState<StoredReport[]>([]);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [openFormId, setOpenFormId] = useState<string>();

  useEffect(() => {
    fetchReports()
      .then((data) => setReports(data.reports))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'レポートを取得できませんでした。'),
      );
  }, []);

  if (reports.length === 0 && !error) {
    return <div className="empty">まだ誤訳レポートがありません。</div>;
  }

  return (
    <div>
      {error && <div className="notice error">{error}</div>}
      {message && <div className="notice">{message}</div>}

      <div className="profile-list">
        {reports.map((report) => (
          <div key={report.id} className="profile-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <div>
              <div className="zh-cell" style={{ fontSize: 15 }}>{report.sourceZh}</div>
              <div>訳: {report.translatedJa}</div>
              {report.suggestedJa && <div>提案: {report.suggestedJa}</div>}
              {report.comment && <div className="hint">コメント: {report.comment}</div>}
              <div className="hint">
                {formatDate(report.createdAt)}・用語集 version {report.glossaryVersion}
              </div>
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <button
                style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}
                onClick={() => setOpenFormId(openFormId === report.id ? undefined : report.id)}
              >
                {openFormId === report.id ? '閉じる' : '用語集に追加'}
              </button>
            </div>
            {openFormId === report.id && (
              <ToGlossaryForm
                report={report}
                onDone={(msg) => {
                  setMessage(msg);
                  setOpenFormId(undefined);
                }}
                onError={setError}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ToGlossaryForm({
  report,
  onDone,
  onError,
}: {
  report: StoredReport;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}): JSX.Element {
  const [sourceZh, setSourceZh] = useState(report.sourceZh);
  // 利用者の提案訳があればそれを初期値にする。それがこの報告の一番の価値。
  const [targetJa, setTargetJa] = useState(report.suggestedJa ?? '');
  const [profile, setProfile] = useState<ProfileId>(report.profiles[0] ?? 'core');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    setBusy(true);
    try {
      await reportToGlossary({ sourceZh: sourceZh.trim(), targetJa: targetJa.trim(), profile });
      onDone(`「${sourceZh.trim()}」を用語集へ追加しました(ingested/reports.csv)。`);
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : '用語集への追加に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 140 }}>
          <label>中国語</label>
          <input value={sourceZh} onChange={(e) => setSourceZh(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 140 }}>
          <label>日本語訳</label>
          <input value={targetJa} onChange={(e) => setTargetJa(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label>プロファイル</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value as ProfileId)}>
            {PROFILE_IDS.map((id) => (
              <option key={id} value={id}>
                {PROFILE_LABELS[id]}
              </option>
            ))}
          </select>
        </div>
        <button
          className="primary"
          onClick={() => void handleSubmit()}
          disabled={busy || sourceZh.trim() === '' || targetJa.trim() === ''}
        >
          {busy ? '追加中…' : '追加'}
        </button>
      </div>
    </div>
  );
}

// ---- 講義メモタブ ----------------------------------------------------------

/**
 * 現地メンバー全員の講義メモを日本から確認するための閲覧専用ビュー。
 * 編集系(タイトル変更・ノート生成)は現地の通常画面に任せる。
 */
function LecturesTab(): JSX.Element {
  const [lectures, setLectures] = useState<LectureMeta[]>([]);
  const [selected, setSelected] = useState<LectureMeta>();
  const [segments, setSegments] = useState<LectureSegment[]>([]);
  const [note, setNote] = useState<string>();
  const [view, setView] = useState<'transcript' | 'note'>('transcript');
  const [error, setError] = useState<string>();

  useEffect(() => {
    fetchLectures()
      .then((data) => setLectures(data.lectures))
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : '講義一覧を取得できませんでした。'),
      );
  }, []);

  const openLecture = (meta: LectureMeta): void => {
    setError(undefined);
    fetchLecture(meta.lectureId)
      .then((data) => {
        setSelected(data.meta);
        setSegments(data.segments);
        setNote(data.note);
        setView('transcript');
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : '講義を取得できませんでした。'),
      );
  };

  if (selected) {
    return (
      <div>
        <div className="row" style={{ marginBottom: 10 }}>
          <button onClick={() => setSelected(undefined)}>← 一覧へ</button>
          <strong style={{ flex: 1 }}>{selected.title}</strong>
        </div>
        <p className="app-subtitle">
          {formatDate(selected.startedAt)}・{selected.segmentCount} 発話・記録:{' '}
          {selected.ownerName || '名前未設定'}
        </p>
        {error && <div className="notice error">{error}</div>}
        <div className="row" style={{ marginBottom: 12 }}>
          <button
            className={view === 'transcript' ? 'primary' : ''}
            onClick={() => setView('transcript')}
          >
            全文
          </button>
          <button className={view === 'note' ? 'primary' : ''} onClick={() => setView('note')} disabled={!note}>
            講義ノート
          </button>
          <div className="spacer" />
          <button
            onClick={() =>
              downloadFile(
                `${selected.lectureId}-transcript.txt`,
                segments.map((s) => `${s.sourceZh}\n${s.translatedJa}\n`).join('\n'),
                'text/plain',
              )
            }
          >
            全文を TXT で保存
          </button>
        </div>
        {view === 'transcript' ? (
          segments.map((s) => (
            <div key={s.segmentId} className="segment">
              <div className="zh">{s.sourceZh}</div>
              <div className="ja" style={{ fontSize: 16 }}>
                {s.translatedJa}
              </div>
            </div>
          ))
        ) : (
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 15,
              lineHeight: 1.7,
              background: 'var(--bg-raised)',
              padding: 16,
              borderRadius: 10,
            }}
          >
            {note}
          </pre>
        )}
      </div>
    );
  }

  return (
    <div>
      {error && <div className="notice error">{error}</div>}
      {lectures.length === 0 && !error ? (
        <div className="empty">まだ講義の記録がありません。</div>
      ) : (
        <div className="profile-list">
          {lectures.map((lecture) => (
            <button
              key={lecture.lectureId}
              className="profile-item"
              style={{ textAlign: 'left', display: 'flex' }}
              onClick={() => openLecture(lecture)}
            >
              <span style={{ flex: 1 }}>
                {lecture.title}
                {lecture.hasNote && ' 📝'}
                <span className="hint" style={{ display: 'block' }}>
                  {formatDate(lecture.updatedAt)}・{lecture.segmentCount} 発話・記録:{' '}
                  {lecture.ownerName || '名前未設定'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
